// The kinds of record an agent's reply links to (its sources), as the backend links them
// (api/agents/refs.py LINKABLE). A registry: adding a kind is one entry here, and one there.
// - icon: shown before the label; screen readers skip it (the label says what the record is);
// - word: what stands in for a record the viewer can't open ("a fax"), in the text and on its chip;
// - opener: what a click does. 'document': the preview shows the document in a frame. 'chart': the
//   preview shows a short card, with `button` going to the chart. 'messages': Messages opens on it,
//   with no preview.
// Channels, contacts and subagents are never linked: their handles stay plain text.
// A label is resolved for each viewer by the server, and is shown only for a record the viewer can
// open: anything else shows its kind's word, never its label.

// The icons aren't ASCII; they are built here so this file stays ASCII.
const FAX = String.fromCodePoint(0x1f4e0);
const PAGE = String.fromCodePoint(0x1f4c4);
const CALENDAR = String.fromCodePoint(0x1f4c5);
const PERSON = String.fromCodePoint(0x1f464);
const CLIPBOARD = String.fromCodePoint(0x1f4cb);

export const REF_KINDS = Object.freeze({
  fax: Object.freeze({ icon: FAX, word: 'a fax', opener: 'document' }),
  report: Object.freeze({ icon: PAGE, word: 'a report', opener: 'document' }),
  visit: Object.freeze({ icon: CALENDAR, word: 'a visit', opener: 'chart', button: 'Open visit' }),
  patient: Object.freeze({ icon: PERSON, word: 'a patient', opener: 'chart', button: 'Open chart' }),
  task: Object.freeze({ icon: CLIPBOARD, word: 'a task', opener: 'messages' }),
});

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

// The registry's entry for a kind, or null for one that is never linked.
export function refKind(kind) {
  return typeof kind === 'string' && Object.prototype.hasOwnProperty.call(REF_KINDS, kind) ? REF_KINDS[kind] : null;
}

// The words that stand in for a ref ("a fax"), or null for a kind that is never linked.
export function refWords(item) {
  return refKind(isObject(item) ? item.kind : null)?.word ?? null;
}

// What a ref's chip says: its label when the viewer can open it and it has one, else its kind's words.
// Null for a kind that is never linked.
export function refLabel(item) {
  const words = refWords(item);
  if (words === null) return null;
  const { label } = item;
  return item.available === true && typeof label === 'string' && label.trim() ? label : words;
}

// The refs of a message that can be shown: objects with a handle (`key`) and a kind that is linked,
// the first one of each handle, in the order given. Anything else in the list is left out.
export function refItems(refs) {
  if (!Array.isArray(refs)) return [];
  const seen = new Set();
  return refs.filter((item) => {
    if (!isObject(item) || typeof item.key !== 'string' || !item.key || !refKind(item.kind) || seen.has(item.key)) {
      return false;
    }
    seen.add(item.key);
    return true;
  });
}
